import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { carryWaiverThroughReschedule } from '@/domain/waiverFlow';
import { sendBookingConfirmation } from '@/domain/bookingConfirmationFlow';
import type { RescheduleReason } from '@/domain/types';

/**
 * Keep-original reschedule flow (Prompt G, 15 Jul ruling). Every reschedule
 * preserves the original booking (marked `superseded`) and requires a reason.
 *
 *  - `move`    — a new dated booking is created for the new slot, linked both
 *                ways, and the paid deposit is repointed to it.
 *  - `release` — "no new date yet": the original is preserved, no new booking
 *                is created, and the paid deposit is unlinked so it surfaces in
 *                the money-cohort queue as a `deposit-on-file` item.
 *
 * "Now" comes from the sim clock, never Date.now(); ids are deterministic.
 */
export type ReschedulePathway =
  | { mode: 'move'; artistId: string; newStartIso: string }
  | { mode: 'release' };

export interface RescheduleInput {
  bookingId: string;
  reason: RescheduleReason;
  /** Free-text note for the reason (required by the UI when reason is 'other'). */
  note?: string;
  pathway: ReschedulePathway;
  by: string;
  at?: string;
}

export interface RescheduleResult {
  ok: boolean;
  supersededBookingId?: string;
  /** Present on the `move` path: the new dated booking. */
  newBookingId?: string;
  /** Present on the `release` path when a paid deposit was unlinked. */
  surfacedDepositId?: string;
  error?: 'no-booking' | 'missing-reason' | 'already-superseded';
}

export function rescheduleBooking(input: RescheduleInput): RescheduleResult {
  const brain = useBrainStore.getState();
  const at = input.at ?? useSimulationStore.getState().clockIso;
  const original = brain.bookings[input.bookingId];
  if (!original) return { ok: false, error: 'no-booking' };
  // Reason is required at the type level AND guarded here, so a caller that
  // forces `undefined` through a cast still cannot commit a reasonless move.
  if (!input.reason) return { ok: false, error: 'missing-reason' };
  if (original.status === 'superseded') return { ok: false, error: 'already-superseded' };

  if (input.pathway.mode === 'move') {
    const chainLen = (original.rescheduleHistory?.length ?? 0) + 1;
    const newBookingId = `bk-resched-${input.bookingId}-${chainLen}`;
    brain.applyReschedule({
      mode: 'move',
      originalId: input.bookingId,
      newBookingId,
      artistId: input.pathway.artistId,
      newStartIso: input.pathway.newStartIso,
      reason: input.reason,
      note: input.note,
      by: input.by,
      at,
    });
    // The waiver travels with the chain onto the new slot (I2): a signed/sent one
    // is preserved, and a move into the T-4d window with none sent dispatches now.
    carryWaiverThroughReschedule(input.bookingId, newBookingId);
    // Updated calendar invite on the new booking's confirmation (I3): same chain-
    // root UID, SEQUENCE+1. The no-date (release) branch sends none.
    sendBookingConfirmation(newBookingId);
    return { ok: true, supersededBookingId: input.bookingId, newBookingId };
  }

  // release: no new date yet. A paid deposit still linked to this booking will
  // be unlinked, surfacing it as a deposit-on-file queue item.
  const dep = original.depositId ? brain.deposits[original.depositId] : undefined;
  const surfacedDepositId =
    dep && dep.status === 'paid' && dep.bookingId === original.id ? original.depositId : undefined;
  brain.applyReschedule({
    mode: 'release',
    originalId: input.bookingId,
    reason: input.reason,
    note: input.note,
    by: input.by,
    at,
  });
  return { ok: true, supersededBookingId: input.bookingId, surfacedDepositId };
}
